import React, { useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogTitle,
    DialogContent,
    IconButton,
    TextField,
} from '@mui/material';
import {
    Close,
} from '@mui/icons-material';
import RegionsPlugin from 'wavesurfer.js/plugins/regions';
import { AuditioBackendManager } from './AuditioBackend';
import { HHMMSSToSeconds } from './TimeUtils';

// Helper function to parse time in MM:SS format for YouTube mode
const parseTime = (timeString: string): number => {
    const parts = timeString.split(':');
    if (parts.length === 2) {
        return parseInt(parts[0]) * 60 + parseInt(parts[1]);
    }
    return 0;
};

type ImportDialogAPIProps = {
    auditioBackendManager: AuditioBackendManager,
    regions?: any,
    wavesurferRef?: any,
    regionRef?: any,
    openImport: boolean,
    setOpenImport: React.Dispatch<React.SetStateAction<boolean>>,
    audioFileId: number,
    mode?: 'youtube' | 'audio',
    onImportComplete?: () => void,
}

const ImportDialogAPI: React.FC<ImportDialogAPIProps> = ({
    auditioBackendManager,
    regions, 
    wavesurferRef, 
    regionRef, 
    openImport, 
    setOpenImport,
    audioFileId,
    mode = 'audio',
    onImportComplete,
}) => {
    const [importText, setImportText] = useState('');

    const handleClose = () => { 
        setOpenImport(false);
    }

    const handleImport = async () => {
        if (audioFileId === 0) {
            console.log('No audio file selected for import');
            return;
        }

        const lines = importText.split('\n');
        const snippetsToCreate = [];

        // Parse all lines and prepare snippet data
        for (const line of lines) {
            if (line.trim() !== "") {
                const values = line.split(',');
                const startTimeStamp = mode === 'youtube' ? parseTime(values[0]) : HHMMSSToSeconds(values[0]);
                const endTimeStamp = mode === 'youtube' ? parseTime(values[1]) : HHMMSSToSeconds(values[1]);
                const snippetName = values[2];

                snippetsToCreate.push({
                    startTime: Number(startTimeStamp),
                    endTime: Number(endTimeStamp),
                    name: snippetName,
                });
            }
        }

        if (mode === 'youtube') {
            // For YouTube mode, use the backend API
            try {
                for (const snippetData of snippetsToCreate) {
                    const snippet = {
                        audioFileId: audioFileId,
                        regionId: `snippet_${Date.now()}_${Math.random()}`,
                        startTime: snippetData.startTime,
                        endTime: snippetData.endTime,
                        name: snippetData.name,
                        isActive: false
                    };

                    const response = await fetch(`${auditioBackendManager.getBaseUrl()}/snippets`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ snippet }),
                    });

                    if (!response.ok) {
                        console.error('Error importing snippet:', response.statusText);
                    }
                }
                
                console.log('✅ All YouTube snippets imported successfully');
                onImportComplete?.();
            } catch (error) {
                console.error('Error importing YouTube snippets:', error);
            }
        } else {
            // For audio mode, use WaveSurfer regions
            if (wavesurferRef?.current && snippetsToCreate.length > 0) {
                const regionsPlugin = wavesurferRef.current.getActivePlugins()[1] as RegionsPlugin;
                
                // Create all regions - WaveSurfer will handle the creation and fire events
                snippetsToCreate.forEach(snippetData => {
                    regionsPlugin.addRegion({
                        start: snippetData.startTime,
                        end: snippetData.endTime,
                        content: snippetData.name,
                        contentEditable: true,
                        drag: true,
                        resize: true,
                    });
                });
                
                console.log('✅ All audio regions queued for creation');
                onImportComplete?.();
            }
        }

        handleClose();
    }

    return (
        <Box>
            <Dialog
                open={openImport}
                onClose={handleClose}
                sx={{ '& .MuiDialog-paper': { width: '75%', maxWidth: 'none' } }}
            >
                <DialogTitle>
                    Import Snippets!
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box
                        display="flex"
                        flexDirection="column"
                        gap="20px"
                    >
                        <TextField
                            label="Snippets CSV"
                            multiline
                            fullWidth
                            variant="standard"
                            value={importText}
                            onChange={(e) => setImportText(e.target.value)}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                        onClick={handleImport}
                        sx={{
                            width: '200px',
                            backgroundColor: 'var(--primary)',
                            color: 'white',
                            boxShadow: '0 2px 8px rgba(85, 203, 205, 0.3)',
                            '&:hover': {
                                backgroundColor: 'var(--primary-dark)',
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 12px rgba(85, 203, 205, 0.4)',
                            },
                            fontWeight: 500,
                            textTransform: 'none',
                            borderRadius: 'var(--radius-md)',
                        }}
                    >
                        Import Snippets
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default ImportDialogAPI; 